import { dateToInputString, getEndOfToday, getStartOfTomorrow, inputStringToDate } from "@/common/helpers/date-input";
import { cn } from "@/common/helpers/utils";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { userConfigStore } from "@/store/user-config-store";
import { CalendarIcon } from "lucide-react";
import React, { useState, type InputHTMLAttributes } from "react";
import type { Matcher } from "react-day-picker";
import { useTranslation } from "react-i18next";

interface DatePickerInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "onBlur" | "type" | "value"
> {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabledDates?: Matcher | Matcher[];
  futureDaysDisabled?: boolean;
  pastDaysDisabled?: boolean;
  ref?: React.Ref<HTMLInputElement>;
}

export function DatePickerInput({
  value,
  onChange,
  disabledDates,
  futureDaysDisabled,
  pastDaysDisabled,
  className,
  disabled,
  onKeyDown,
  onBlur,
  ref,
  ...inputProps
}: DatePickerInputProps) {
  const { t } = useTranslation("common");
  const dateFormat = userConfigStore((state) => state.dateFormat);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const selectedDate = inputStringToDate(value, { dateFormat });
  const [month, setMonth] = useState<Date | undefined>(selectedDate);

  const today = getEndOfToday();
  const tomorrow = getStartOfTomorrow();
  const anchorYear = selectedDate?.getFullYear() ?? month?.getFullYear() ?? today.getFullYear();
  const startMonth = new Date(anchorYear - 50, 0, 1);
  const endMonth = new Date(anchorYear + 50, 11, 31);
  const disabledMatchers = [
    ...(disabledDates ? (Array.isArray(disabledDates) ? disabledDates : [disabledDates]) : []),
    ...(pastDaysDisabled ? [{ before: tomorrow }] : []),
    ...(futureDaysDisabled ? [{ after: today }] : []),
  ];

  const placeholder = dateFormat;

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const nextValue = event.target.value;
    onChange(nextValue);
    const parsedValue = inputStringToDate(nextValue, { dateFormat });
    if (parsedValue) {
      setMonth(parsedValue);
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!disabled && event.key === "ArrowDown") {
      event.preventDefault();
      setIsCalendarOpen(true);
    }
    onKeyDown?.(event);
  }

  function handleCalendarOpenChange(nextOpen: boolean) {
    if (nextOpen && selectedDate) {
      setMonth(selectedDate);
    }
    setIsCalendarOpen(nextOpen);
  }

  function handleCalendarSelect(date: Date | undefined) {
    onChange(dateToInputString(date, { dateFormat }));
    onBlur?.();
    if (date) {
      setMonth(date);
    }
    setIsCalendarOpen(false);
  }

  return (
    <div className={cn("relative", className)}>
      <Input
        {...inputProps}
        ref={ref}
        type="text"
        value={value}
        placeholder={placeholder}
        className="px-1.5 pr-7"
        disabled={disabled}
        onChange={handleInputChange}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
      />
      <Popover open={isCalendarOpen} onOpenChange={handleCalendarOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={t("SelectDate")}
            disabled={disabled}
            className="text-muted-foreground hover:text-foreground absolute top-0 right-0 flex h-9 w-7 cursor-pointer items-center justify-center disabled:cursor-not-allowed disabled:opacity-50"
          >
            <CalendarIcon className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto overflow-hidden p-0" align="end" alignOffset={-8} sideOffset={10}>
          <Calendar
            mode="single"
            selected={selectedDate}
            month={month}
            onMonthChange={setMonth}
            captionLayout="dropdown"
            startMonth={startMonth}
            endMonth={endMonth}
            disabled={disabledMatchers}
            autoFocus
            onSelect={handleCalendarSelect}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
